/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSidenav }            from '@angular/material/sidenav';
import { first }                 from 'rxjs/operators';

import { Logger }                from '@/util/logger';
import { Job }                   from '@/models/job';
import { PanoramaService }       from '@/services/panorama.service';
import { errorAsText }           from '@/util/util';
import { AlertService }          from '@/services/alert.service';
import {SortEvent} from "primeng/api";

@Component({
  standalone: false,
  templateUrl: 'sync.jobs.component.html'
})

/**
 *
 * @class SyncJobsComponent
 *
 * @brief sync jobs page - used to sync BigQuery panorama data to the local postgresql database.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2022
 *
 */
export class SyncJobsComponent implements OnInit {

  @ViewChild('adminsidenav')
  public nav: MatSidenav;

  /** can we do a new sync job? */
  public enabled: boolean = false;

  /** the HTML presentation of the job list */
  public presentation: {id: string, header: string, body: string}[] = [];

  /** the refresh timer ID */
  public timer: number|null = null;

  /** standard constructor */
  constructor(
    private panos:  PanoramaService,
    private alerts: AlertService,
    private cdRef:  ChangeDetectorRef
  ) {


  }

  /** render the sync jobs history */
  renderJobs(jobs: Job[]) {

    const sj: SyncJobsComponent = this;

    /* start fresh */

    sj.presentation = [];

    /*
     * if any are in creating or importing status, we have to disable syncing.
     * we only allow exactly one sync to be happenting at a time.
     *
     */

    const runCount: number =
      jobs.filter(x => (x.status === 'creating') || (x.status === 'importing')).length;

    const current: Job = jobs.find(x => (x.status === 'creating') || (x.status === 'importing'));

    sj.enabled = true;

    if (runCount) {

      sj.enabled = false;

      Logger.debug(`[SyncJobsComponent] sync is running: `, current);

      /*
       * if they re-load the page in the middle of a running sync, launch the refresh
       * timer to keep the page up to date.
       *
       */

      if (!sj.timer) {

        sj.timer = window.setInterval(() => {

          sj.refresh();

        }, 300);
      }

    } else {

      Logger.debug(`[SyncJobsComponent] no current sync.`);

      if (sj.timer) {

        window.clearInterval(sj.timer);

        sj.timer = null;
      }
    }

    /* build the presentation */

    for (const job of jobs) {

      const panel: { id: string, header: string, body: string } = {
        id: job.id,
        header: '',
        body: ''
      };

      /* the body of the panel is always a list of the job output lines */

      panel.body = `<span class="joblistpanelitembody"><pre>`;

      for (const line of job.meta.output) {

        panel.body += line + `</br>`;
      }

      panel.body += `</br></br`;

      panel.body += `</pre></span>`;

      /* header is a little more work, have show what's appropriate for its status */

      panel.header = `<span class="sjpanelgrey">[</span><b>${job.id}</b><span class="sjpanelgrey">]</span> - `;
      panel.header += `<b>${job.author}</b> - `;

      if (job.status === 'completed') {

        panel.header += ` <span class="sjpanelgreen">COMPLETE</span> | `;

        panel.header += `<span class="sjpanelgrey">${job.updated_at.toLocaleString()}</span> | `;

        /* add in the metrics */

        panel.header += ` <span class="sjpanelgrey">total=</span>${job.meta.metrics.total}<span class="sjpanelgrey">, enabled=</span>${job.meta.metrics.enabled}<span class="sjpanelgrey">, disabled=</span>${job.meta.metrics.disabled}<span class="sjpanelgrey">, time=</span>${job.duration} sec`

      } else if ((job.status === 'failed') || (job.status === 'error')) {

        panel.header += ` <span class="sjpanelred">FAILED</span> | `;

        panel.header += `<span class="sjpanelred">${job.error}</span>`;

      } else {

        panel.header += ` <span>IN PROGRESS</span> | `;

        /* show the most recent output line */

        let line: string = '';

        if (job.meta.output.length) {

          line = job.meta.output[job.meta.output.length - 1];
        }

        panel.header += `<span class="sjpanelyellow">${line}</span>`;
      }

      panel.header += ``;

      /* save this panel */

      sj.presentation.push(panel);
    }

  }

  /** fetch most recent jobs */
  refresh() {

    /* fetch the most recent jobs ... */

    const sj: SyncJobsComponent = this;

    Logger.debug('[SyncJobsComponent] refreshing...');

    sj.panos.recentJobs(5)
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          const jobs: Job[] = [];

          for (const item of data.result) {

            const t1 = item.created_at.split(/[- :]/);
            item.created_at = new Date(Date.UTC(t1[0], t1[1] - 1, t1[2], t1[3], t1[4], t1[5]));

            const t2 = item.updated_at.split(/[- :]/);
            item.updated_at = new Date(Date.UTC(t2[0], t2[1] - 1, t2[2], t2[3], t2[4], t2[5]));

            jobs.push(new Job(item));
          }

          Logger.debug(`[SyncJobsComponent] new job list: `, jobs);

          /* render the job list, set sync enablement etc. */

          sj.renderJobs(jobs);

          sj.cdRef.detectChanges();
        },

        error: (error: any) => {

          /* show the user the error */

          error = errorAsText(error);

          Logger.error(`[SyncJobsComponent] could not refresh: `, error);

          sj.alerts.error(error);
        }

      });

  }

  /** on page setup */
  ngOnInit() {

    const sj: SyncJobsComponent = this;

    Logger.debug('[SyncJobsComponent] init...');

    sj.refresh();

  }

  public sync() {

    const sj: SyncJobsComponent = this;

    Logger.debug('[SyncJobsComponent] syncing...');

    sj.panos.sync()
      .pipe(first())
      .subscribe({

        next: (data: any) => {

          Logger.error(`[SyncJobsComponent] sync launched: `, data);

          /* start a refresh timer to keep updating the page until the sync is done */

          sj.timer = window.setInterval(() => {

            sj.refresh();

          }, 300);

        },

        error: (error: any) => {

          error = errorAsText(error);

          Logger.error(`[SyncJobsComponent] could not launch sync: `, error);

          sj.alerts.error(error);
        }

      });

  }

  /** expand/collapse the sidebar navigation panel */
  toggleSidebar() {

    this.nav.toggle();
  }

}
